import { useEffect, useCallback } from 'react';

/**
 * Keyboard shortcut definition
 */
interface KeyboardShortcut {
  /**
   * Key combination (e.g., 'g h' for go home, 'ctrl+k' for search)
   */
  key: string;
  /**
   * Handler function to execute
   */
  handler: () => void;
  /**
   * Description for accessibility
   */
  description: string;
  /**
   * Whether to prevent default behavior
   */
  preventDefault?: boolean;
}

/**
 * Options for keyboard navigation
 */
interface KeyboardNavigationOptions {
  /**
   * Array of keyboard shortcuts
   */
  shortcuts?: KeyboardShortcut[];
  /**
   * Whether to enable arrow key navigation
   */
  enableArrowNavigation?: boolean;
  /**
   * Selector for navigable items
   */
  itemSelector?: string;
  /**
   * Whether navigation wraps at boundaries
   */
  wrapNavigation?: boolean;
}

/**
 * Hook for keyboard navigation and shortcuts
 */
export const useKeyboardNavigation = (options: KeyboardNavigationOptions = {}) => {
  const {
    shortcuts = [],
    enableArrowNavigation = false,
    itemSelector = '[tabindex]:not([tabindex="-1"]), a, button, input, select, textarea',
    wrapNavigation = true,
  } = options;

  // Handle keyboard shortcuts
  useEffect(() => {
    const keySequence: string[] = [];
    let sequenceTimer: NodeJS.Timeout;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Build key combination string
      const modifiers = [];
      if (e.ctrlKey) modifiers.push('ctrl');
      if (e.altKey) modifiers.push('alt');
      if (e.shiftKey) modifiers.push('shift');
      if (e.metaKey) modifiers.push('meta');
      
      const key = e.key.toLowerCase();
      const combination = [...modifiers, key].join('+');

      // Check single key shortcuts
      const singleKeyShortcut = shortcuts.find(s => s.key === combination);
      if (singleKeyShortcut) {
        if (singleKeyShortcut.preventDefault) {
          e.preventDefault();
        }
        singleKeyShortcut.handler();
        return;
      }

      // Handle key sequences (e.g., 'g h' for go home)
      keySequence.push(combination);
      clearTimeout(sequenceTimer);
      
      sequenceTimer = setTimeout(() => {
        keySequence.length = 0;
      }, 1000);

      const sequence = keySequence.join(' ');
      const sequenceShortcut = shortcuts.find(s => s.key === sequence);
      if (sequenceShortcut) {
        if (sequenceShortcut.preventDefault) {
          e.preventDefault();
        }
        sequenceShortcut.handler();
        keySequence.length = 0;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(sequenceTimer);
    };
  }, [shortcuts]);

  // Handle arrow key navigation
  useEffect(() => {
    if (!enableArrowNavigation) return;

    const handleArrowNavigation = (e: KeyboardEvent) => {
      // Only handle arrow keys
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return;
      }

      // Don't interfere with form inputs
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        return;
      }

      const navigableItems = Array.from(
        document.querySelectorAll<HTMLElement>(itemSelector)
      ).filter(el => !el.hasAttribute('disabled'));

      if (navigableItems.length === 0) return;

      const currentIndex = navigableItems.findIndex(el => el === document.activeElement);
      let nextIndex = currentIndex;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          nextIndex = currentIndex + 1;
          if (nextIndex >= navigableItems.length) {
            nextIndex = wrapNavigation ? 0 : navigableItems.length - 1;
          }
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) {
            nextIndex = wrapNavigation ? navigableItems.length - 1 : 0;
          }
          break;
      }

      if (nextIndex !== currentIndex) {
        e.preventDefault();
        navigableItems[nextIndex].focus();
      }
    };

    document.addEventListener('keydown', handleArrowNavigation);

    return () => {
      document.removeEventListener('keydown', handleArrowNavigation);
    };
  }, [enableArrowNavigation, itemSelector, wrapNavigation]);

  /**
   * Register a keyboard shortcut programmatically
   */
  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    // This would need to be implemented with state management
    // For now, shortcuts should be passed via options
    console.warn('Dynamic shortcut registration not yet implemented');
  }, []);

  /**
   * Show keyboard shortcuts help
   */
  const showKeyboardHelp = useCallback(() => {
    const helpText = shortcuts
      .map(s => `${s.key}: ${s.description}`)
      .join('\n');
    
    // Create and show help dialog
    const dialog = document.createElement('dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-label', 'Keyboard shortcuts');
    dialog.innerHTML = `
      <div style="padding: 20px; max-width: 400px;">
        <h2 style="margin-top: 0;">Keyboard Shortcuts</h2>
        <pre style="white-space: pre-wrap;">${helpText}</pre>
        <button onclick="this.closest('dialog').close()" style="margin-top: 10px;">Close</button>
      </div>
    `;
    document.body.appendChild(dialog);
    dialog.showModal();
    
    dialog.addEventListener('close', () => {
      document.body.removeChild(dialog);
    });
  }, [shortcuts]);

  return {
    registerShortcut,
    showKeyboardHelp,
  };
};

/**
 * Common keyboard shortcuts for the application
 */
export const commonShortcuts: KeyboardShortcut[] = [
  {
    key: 'alt+h',
    handler: () => window.location.href = '/dashboard',
    description: 'Go to home/dashboard',
    preventDefault: true,
  },
  {
    key: 'alt+c',
    handler: () => window.location.href = '/clients/new',
    description: 'Create new client',
    preventDefault: true,
  },
  {
    key: 'alt+d',
    handler: () => window.location.href = '/dimensions',
    description: 'Go to dimensions',
    preventDefault: true,
  },
  {
    key: '/',
    handler: () => {
      const searchInput = document.querySelector<HTMLInputElement>('[type="search"], [placeholder*="Search"]');
      if (searchInput) {
        searchInput.focus();
      }
    },
    description: 'Focus search',
    preventDefault: true,
  },
  {
    key: 'escape',
    handler: () => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement) {
        activeElement.blur();
      }
    },
    description: 'Clear focus',
  },
];